import React from 'react';
import { PlusIcon, Trash2Icon, ArrowUpIcon, ArrowDownIcon } from 'lucide-react';
import { Project } from '../../../lib/types';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Button } from '../../../components/ui/Button';
interface ContentTabProps {
  project: Project;
  updateField: (field: keyof Project, value: any) => void;
}
export function ContentTab({
  project,
  updateField
}: ContentTabProps) {
  return <div className="space-y-12 pb-24" data-id="element-990">
      {/* Basic Info */}
      <section className="space-y-6" data-id="element-991">
        <div className="border-b border-border pb-2" data-id="element-992">
          <h2 className="font-display text-2xl font-bold" data-id="element-993">Basic Info</h2>
          <p className="font-mono text-xs text-muted-foreground mt-1" data-id="element-994">
            Core details for the project card and header.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-995">
          <Input label="Project Title" value={project.title} onChange={e => updateField('title', e.target.value)} placeholder="e.g. Fintech Trading Platform" data-id="element-996" />
          <Input label="Project Number" value={project.num} onChange={e => updateField('num', e.target.value)} placeholder="e.g. 01" data-id="element-997" />
          <Input label="Year" value={project.year} onChange={e => updateField('year', e.target.value)} placeholder="e.g. 2025" data-id="element-998" />
          <Input label="Category / Industry" value={project.category} onChange={e => updateField('category', e.target.value)} placeholder="e.g. Fintech, Web3, E-commerce" data-id="element-999" />
          <Input label="Role" value={project.role} onChange={e => updateField('role', e.target.value)} placeholder="e.g. Lead Product Designer" data-id="element-1000" />
          <Input label="Tools (comma separated)" value={project.tools.join(', ')} onChange={e => updateField('tools', e.target.value.split(',').map(t => t.trim()))} placeholder="e.g. Figma, React, Framer" data-id="element-1001" />
        </div>

        <Input label="Subtitle" value={project.subtitle} onChange={e => updateField('subtitle', e.target.value)} placeholder="A catchy one-liner for the hero section" data-id="element-1002" />

        <Textarea label="Short Summary" value={project.summary} onChange={e => updateField('summary', e.target.value)} placeholder="Brief description for the project card on the homepage" rows={3} data-id="element-1003" />
      </section>

      {/* Case Study Details */}
      <section className="space-y-6" data-id="element-1004">
        <div className="border-b border-border pb-2" data-id="element-1005">
          <h2 className="font-display text-2xl font-bold" data-id="element-1006">
            Case Study Details
          </h2>
          <p className="font-mono text-xs text-muted-foreground mt-1" data-id="element-1007">
            Structured content for the default case study layout.
          </p>
        </div>

        <Textarea label="Overview" value={project.overview} onChange={e => updateField('overview', e.target.value)} placeholder="High-level overview of the project" rows={4} data-id="element-1008" />

        <Textarea label="The Challenge" value={project.challenge} onChange={e => updateField('challenge', e.target.value)} placeholder="What problem were you trying to solve?" rows={4} data-id="element-1009" />

        <Textarea label="The Approach" value={project.approach} onChange={e => updateField('approach', e.target.value)} placeholder="How did you tackle the problem?" rows={4} data-id="element-1010" />

        <Textarea label="The Outcome" value={project.outcome} onChange={e => updateField('outcome', e.target.value)} placeholder="What was the final result?" rows={4} data-id="element-1011" />

        <div className="space-y-2" data-id="element-1012">
          <label className="font-mono text-xs text-muted-foreground uppercase tracking-widest" data-id="element-1013">
            Key Metrics / Results (one per line)
          </label>
          <Textarea value={project.metrics.join('\n')} onChange={e => updateField('metrics', e.target.value.split('\n').filter(m => m.trim() !== ''))} placeholder="e.g. 40% increase in conversion&#10;2.5x faster load times" rows={4} data-id="element-1014" />
        </div>

        <Textarea label="Lessons Learned" value={project.lessonsLearned} onChange={e => updateField('lessonsLearned', e.target.value)} placeholder="What did you learn from this project?" rows={4} data-id="element-1015" />

        <Textarea label="Next Steps" value={project.nextSteps} onChange={e => updateField('nextSteps', e.target.value)} placeholder="What are the future plans for this project?" rows={4} data-id="element-1016" />
      </section>

      {/* Project Images */}
      <section className="space-y-6" data-id="element-1017">
        <div className="border-b border-border pb-2" data-id="element-1018">
          <div className="flex items-center justify-between" data-id="element-1019">
            <div data-id="element-1020">
              <h2 className="font-display text-2xl font-bold" data-id="element-1021">
                Project Images
              </h2>
              <p className="font-mono text-xs text-muted-foreground mt-1" data-id="element-1022">
                Screenshots, mockups, and visuals from the project. These appear
                in the project gallery.
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={() => updateField('projectImages', [...(project.projectImages || []), {
            url: '',
            alt: '',
            caption: ''
          }])} data-id="element-1023">
              <PlusIcon className="w-4 h-4 mr-2" data-id="element-1024" /> Add Image
            </Button>
          </div>
        </div>

        <div className="space-y-4" data-id="element-1025">
          {(project.projectImages || []).length === 0 && <div className="border border-dashed border-border p-8 text-center" data-id="element-1026">
              <p className="font-mono text-xs text-muted-foreground" data-id="element-1027">
                No project images yet. Add screenshots, mockups, or design work
                to showcase.
              </p>
            </div>}
          {(project.projectImages || []).map((img, i) => <div key={i} className="border border-border bg-card p-4 space-y-3" data-id="element-1028">
              <div className="flex items-start gap-3" data-id="element-1029">
                {/* Preview */}
                {img.url && <div className="w-24 h-24 shrink-0 border border-border bg-background overflow-hidden" data-id="element-1030">
                    <img src={img.url} alt={img.alt || ''} className="w-full h-full object-cover" data-id="element-1031" />
                  </div>}
                <div className="flex-1 space-y-3" data-id="element-1032">
                  <Input label="Image URL" value={img.url} onChange={e => {
                const updated = [...(project.projectImages || [])];
                updated[i] = {
                  ...updated[i],
                  url: e.target.value
                };
                updateField('projectImages', updated);
              }} placeholder="https://... or paste image URL" data-id="element-1033" />
                  <div className="grid grid-cols-2 gap-3" data-id="element-1034">
                    <Input label="Alt Text" value={img.alt} onChange={e => {
                  const updated = [...(project.projectImages || [])];
                  updated[i] = {
                    ...updated[i],
                    alt: e.target.value
                  };
                  updateField('projectImages', updated);
                }} placeholder="Describe the image" data-id="element-1035" />
                    <Input label="Caption (optional)" value={img.caption} onChange={e => {
                  const updated = [...(project.projectImages || [])];
                  updated[i] = {
                    ...updated[i],
                    caption: e.target.value
                  };
                  updateField('projectImages', updated);
                }} placeholder="Visible caption below image" data-id="element-1036" />
                  </div>
                </div>
                <div className="flex flex-col gap-1 shrink-0" data-id="element-1037">
                  <button onClick={() => {
                if (i === 0) return;
                const updated = [...(project.projectImages || [])];
                [updated[i - 1], updated[i]] = [updated[i], updated[i - 1]];
                updateField('projectImages', updated);
              }} disabled={i === 0} className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30" data-id="element-1038">
                    <ArrowUpIcon className="w-4 h-4" data-id="element-1039" />
                  </button>
                  <button onClick={() => {
                if (i === (project.projectImages || []).length - 1) return;
                const updated = [...(project.projectImages || [])];
                [updated[i], updated[i + 1]] = [updated[i + 1], updated[i]];
                updateField('projectImages', updated);
              }} disabled={i === (project.projectImages || []).length - 1} className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30" data-id="element-1040">
                    <ArrowDownIcon className="w-4 h-4" data-id="element-1041" />
                  </button>
                  <button onClick={() => {
                const updated = [...(project.projectImages || [])];
                updated.splice(i, 1);
                updateField('projectImages', updated);
              }} className="p-1 text-destructive hover:text-destructive/80" data-id="element-1042">
                    <Trash2Icon className="w-4 h-4" data-id="element-1043" />
                  </button>
                </div>
              </div>
            </div>)}
        </div>
      </section>

      {/* Links & CTA */}
      <section className="space-y-6" data-id="element-1044">
        <div className="border-b border-border pb-2" data-id="element-1045">
          <h2 className="font-display text-2xl font-bold" data-id="element-1046">Links & CTA</h2>
          <p className="font-mono text-xs text-muted-foreground mt-1" data-id="element-1047">
            External links and call-to-action settings.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1048">
          <Input label="Live Project URL" value={project.liveUrl} onChange={e => updateField('liveUrl', e.target.value)} placeholder="https://..." data-id="element-1049" />
          <Input label="Prototype URL" value={project.prototypeUrl} onChange={e => updateField('prototypeUrl', e.target.value)} placeholder="https://figma.com/..." data-id="element-1050" />
          <Input label="CTA Button Text" value={project.ctaText} onChange={e => updateField('ctaText', e.target.value)} placeholder="e.g. View Live Site" data-id="element-1051" />
          <Input label="CTA Button Link" value={project.ctaLink} onChange={e => updateField('ctaLink', e.target.value)} placeholder="URL for the CTA button" data-id="element-1052" />
        </div>
      </section>
    </div>;
}